import React, { useContext, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Button,
    Table,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    UncontrolledTooltip,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { ButtonLoader } from '../../../components/Loader.js';
import Pagination from '../../../components/Pagination.js';
import { SelectedFacilitiesContext } from '../../../contexts/SelectedFacilitiesContext.js';
import { UserContext } from '../../../contexts/UserContext.js';
import useSavedOrderReturns from '../../../services/orderReturns/useSavedOrderReturns.js';
import useDeleteOrderReturn from '../../../services/orderReturns/useDeleteOrderReturn.js';
import useExportFile from '../../../services/commonServices/useExportFile.js';
import usePrintPDF from '../../../services/commonServices/usePrintPDF.js';
import CreatePDFModal from './components/CreatePDFModal.js';
import { hasRolePermission } from '../../../utility-services/rolePermissions.js';

function pick(obj, keys, fallback = '-') {
    if (obj == null || typeof obj !== 'object') return fallback;
    for (const k of keys) {
        if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
        const v = obj[k];
        if (v !== undefined && v !== null && v !== '') return v;
        if (v === 0) return 0;
    }
    return fallback;
}

const getParentId = (item) => item?.orderReturnId ?? item?.OrderReturnId ?? item?.id ?? null;

const formatDate = (v) =>
    v
        ? new Date(v).toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
          })
        : '-';

function SavedOrderReturns() {
    const history = useHistory();
    const { selectedFacilities } = useContext(SelectedFacilitiesContext);
    const { user, roles } = useContext(UserContext);
    const canDeleteOrderReturn = hasRolePermission(roles, 'canDeleteOrderReturns');
    const canEditOrderReturn = hasRolePermission(roles, 'canEditOrderReturns');

    const facilityIds = useMemo(() => {
        return (selectedFacilities || []).map((f) => f?.facID).filter((id) => id != null && id !== '');
    }, [selectedFacilities]);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(10);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const detailRef = React.useRef(null);

    const { savedOrderReturnsRaw, isSavedOrderReturnsLoading, refetchSavedOrderReturns } = useSavedOrderReturns(
        facilityIds,
        pageNumber,
        pageSize
    );

    const rows = savedOrderReturnsRaw?.items ?? [];
    const totalCount = Number(savedOrderReturnsRaw?.totalCount ?? rows.length ?? 0);
    const totalPages = Number(
        savedOrderReturnsRaw?.totalPages ?? Math.max(1, Math.ceil(totalCount / (pageSize || 10)))
    );

    const [myReturnsMode, setMyReturnsMode] = useState(false);
    const allRowsPageSize = Math.max(totalCount || pageSize, pageSize);
    const { savedOrderReturnsRaw: allSavedOrderReturnsRaw, isSavedOrderReturnsLoading: allSavedOrderReturnsLoading } =
        useSavedOrderReturns(facilityIds, 1, allRowsPageSize);
    const allRows = allSavedOrderReturnsRaw?.items ?? [];

    const normalizeValue = (value) => (value === undefined || value === null ? '' : String(value).trim().toLowerCase());
    const currentUserIds = useMemo(
        () => [user?.userId, user?.id, user?.UserId].map(normalizeValue).filter(Boolean),
        [user]
    );
    const isCurrentUserRecord = (item) => {
        const ids = [item?.userId, item?.UserId].map(normalizeValue).filter(Boolean);
        return ids.some((v) => currentUserIds.includes(v));
    };
    const allMyReturns = useMemo(() => allRows.filter(isCurrentUserRecord), [allRows, currentUserIds]);
    const isMyReturnsSelected =
        allMyReturns.length > 0 &&
        allMyReturns.every((row) => selectedRows.some((r) => getParentId(r) === getParentId(row)));

    const handleSelectOnlyMyReturns = () => {
        if (myReturnsMode) {
            setSelectedRows([]);
            setMyReturnsMode(false);
        } else {
            setSelectedRows(allMyReturns);
            setMyReturnsMode(true);
        }
    };

    React.useEffect(() => {
        if (!myReturnsMode) return;
        setSelectedRows(allMyReturns);
    }, [allMyReturns, myReturnsMode]);

    React.useEffect(() => {
        if (selectedRecord && detailRef.current) {
            detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedRecord]);

    const selectedItems = selectedRecord?.items ?? [];

    const toggleRowSelection = (item) => {
        const id = getParentId(item);
        setSelectedRows((prev) =>
            prev.some((r) => getParentId(r) === id) ? prev.filter((r) => getParentId(r) !== id) : [...prev, item]
        );
    };

    const handleEditReturn = (parent) => {
        const rawItems = Array.isArray(parent?.items) ? parent.items : [];
        if (rawItems.length === 0) return;
        const items = rawItems.map((item) => ({
            itemId: item?.id ?? 0,
            rxNumber: item?.rxNumber ?? '',
            facId: item?.facId ?? '',
            residentName: item?.patientName ?? '',
            residentId: item?.patientId ?? '',
            drugName: item?.drugLabelName ?? '',
            dispenseDate: item?.dispenseDate ?? '',
            quantity: item?.amount ?? '',
            allowReturn: item?.allowReturn ?? true,
            blockReturnReason: item?.blockReturnReason ?? '',
            controlledDrug: item?.controlledDrug ?? false,
            isManual: item?.isManual ?? item?.IsManual ?? false,
            destructionOnly: item?.isDestructionOnly ?? item?.IsDestructionOnly ?? false,
        }));
        history.push({
            pathname: '/utilities/order-return',
            state: {
                editItems: items,
                facId: pick(parent, ['facId'], ''),
                orderReturnId: getParentId(parent),
            },
        });
    };

    const selectedItemsForExport = useMemo(
        () => selectedRows.flatMap((row) => (Array.isArray(row?.items) ? row.items : [])),
        [selectedRows]
    );
    const hasSelectedItemRows = selectedItemsForExport.length > 0;
    const exportDisabledTooltip = 'Select one or more table rows to enable export options.';
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleDropdown = () => {
        if (!hasSelectedItemRows) return;
        setDropdownOpen(!dropdownOpen);
    };

    const hasMultipleSelectedItemFacilities = () => {
        const uniqueFacilityIds = new Set(
            selectedItemsForExport
                .map((item) => item?.facId)
                .filter((facId) => facId !== undefined && facId !== null && facId !== '')
        );

        return uniqueFacilityIds.size > 1;
    };

    const showSingleFacilityValidation = () => {
        Swal.fire({
            icon: 'info',
            text: 'Please select records from 1 facility before proceeding.',
            customClass: {
                confirmButton: 'custom-green-button',
            },
        });
    };

    const buildExportParams = (selectedIds) => {
        const params = new URLSearchParams();
        selectedIds.forEach((id) => params.append('ids', id));
        params.append('exportType', 'Excel');
        return params;
    };

    const { exportFile, isExportFileLoading } = useExportFile();
    const [activeExport, setActiveExport] = useState(null);

    const handleExportItems = (type, url, fileName) => {
        if (!hasSelectedItemRows) {
            Swal.fire({
                icon: 'info',
                text: exportDisabledTooltip,
                customClass: { confirmButton: 'custom-green-button' },
            });
            return;
        }

        if (hasMultipleSelectedItemFacilities()) {
            showSingleFacilityValidation();
            return;
        }

        const selectedIds = selectedItemsForExport
            .map((item) => item?.id)
            .filter((id) => id !== undefined && id !== null && id !== '');

        setActiveExport(type);
        exportFile(
            { url, fileName, params: buildExportParams(selectedIds) },
            {
                onSuccess: () => {
                    setSelectedRows([]);
                    setSelectedRecord(null);
                    refetchSavedOrderReturns();
                },
                onSettled: () => setActiveExport(null),
            }
        );
    };

    const { printPDF, isPrintPDFLoading, printPDFResponse } = usePrintPDF();
    const [isCreatePDFModalOpen, setIsCreatePDFModalOpen] = useState(false);

    const { deleteOrderReturn, isDeleteOrderReturnLoading } = useDeleteOrderReturn();

    const handleDeleteReturn = (parent) => {
        const items = Array.isArray(parent?.items) ? parent.items : [];
        const itemIds = items.map((item) => item?.id).filter((id) => id !== undefined && id !== null);
        if (itemIds.length === 0) return;

        const parentId = getParentId(parent);
        Swal.fire({
            title: 'Delete saved return?',
            text: `This will permanently delete return #${parentId} and all ${itemIds.length} of its line items.`,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Delete',
            customClass: { confirmButton: 'custom-green-button', cancelButton: 'custom-cancel-button' },
        }).then((result) => {
            if (!result.isConfirmed) return;
            deleteOrderReturn(itemIds, {
                onSuccess: () => {
                    setSelectedRecord(null);
                    setSelectedRows((prev) => prev.filter((r) => getParentId(r) !== parentId));
                    refetchSavedOrderReturns();
                },
            });
        });
    };

    const handlePrintItems = () => {
        if (!hasSelectedItemRows) {
            Swal.fire({
                icon: 'info',
                text: exportDisabledTooltip,
                customClass: { confirmButton: 'custom-green-button' },
            });
            return;
        }

        if (hasMultipleSelectedItemFacilities()) {
            showSingleFacilityValidation();
            return;
        }

        printPDF(
            {
                url: `/orderReturns/packingSlip`,
                fileName: 'packing-slip.pdf',
                downloadOnSuccess: false,
                payload: {
                    UserId: user.userId,
                    OrderReturnItemIds: selectedItemsForExport.map((item) => item.id),
                },
            },
            {
                onSuccess: () => {
                    setIsCreatePDFModalOpen(true);
                },
            }
        );
    };

    return (
        <div className="page-content">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="mb-0">Saved Returns</h2>
                <Button style={{ marginTop: '10px' }} color="secondary" outline tag={Link} to="/utilities/order-return">
                    Back to order return
                </Button>
            </div>
            <Card className="p-2">
                <Row>
                    <Col md={12}>
                        {isSavedOrderReturnsLoading ? (
                            <div className="text-center p-5">
                                <ButtonLoader />
                            </div>
                        ) : (
                            <>
                                <Table className="table-bordered" responsive>
                                    <thead className="thead-light">
                                        <tr>
                                            <th style={{ width: '40px', textAlign: 'center' }} aria-label="Select" />
                                            <th>Return ID</th>
                                            <th>Facility</th>
                                            <th>Form Date</th>
                                            <th>Items Count</th>
                                            <th>Created By</th>
                                            <th>Updated By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length > 0 ? (
                                            rows.map((item, index) => {
                                                const id = getParentId(item);
                                                const isSelected = id != null && getParentId(selectedRecord) === id;
                                                const isChecked = selectedRows.some((r) => getParentId(r) === id);
                                                return (
                                                    <tr
                                                        key={id ?? index}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: isSelected ? '#e8f0ff' : undefined,
                                                        }}
                                                        onClick={() => setSelectedRecord(isSelected ? null : item)}>
                                                        <td
                                                            style={{ textAlign: 'center' }}
                                                            onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => toggleRowSelection(item)}
                                                            />
                                                        </td>
                                                        <td>{id ?? '-'}</td>
                                                        <td>{pick(item, ['facId'])}</td>
                                                        <td>{formatDate(pick(item, ['slipDate'], null))}</td>
                                                        <td>{Array.isArray(item?.items) ? item.items.length : 0}</td>
                                                        <td>{pick(item, ['createdBy'])}</td>
                                                        <td>{pick(item, ['updatedBy'])}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center p-4">
                                                    No saved returns found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                <div className="mt-2">
                                    <Pagination
                                        pageNumber={pageNumber}
                                        pageSize={pageSize}
                                        totalCount={totalCount}
                                        totalPages={totalPages}
                                        onPageChange={(p) => setPageNumber(p)}
                                    />
                                </div>
                                <div className="d-flex justify-content-between align-items-center px-2 mt-2">
                                    <div className="d-flex align-items-center">
                                        <Button
                                            color="gray"
                                            style={{
                                                cursor: 'pointer',
                                                border: '1px solid #E5E7EB',
                                                borderRadius: '5px',
                                                padding: '10px 10px',
                                                color: '#6B7280',
                                            }}
                                            className="btn-sm"
                                            onClick={handleSelectOnlyMyReturns}
                                            disabled={allSavedOrderReturnsLoading || allMyReturns.length === 0}>
                                            {isMyReturnsSelected
                                                ? 'Unselect Only My Returns'
                                                : 'Select Only My Returns'}
                                        </Button>
                                        <div style={{ fontSize: '13px' }} className="text-muted ml-2">
                                            {selectedRows?.length || 0} records selected
                                        </div>
                                    </div>
                                    <div>
                                        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                                            <span id="order-return-items-export-tooltip" className="d-inline-block">
                                                <DropdownToggle
                                                    caret
                                                    color="primary"
                                                    className="btn-sm"
                                                    disabled={!hasSelectedItemRows}
                                                    style={
                                                        !hasSelectedItemRows
                                                            ? { pointerEvents: 'none', opacity: 1 }
                                                            : undefined
                                                    }>
                                                    Export
                                                </DropdownToggle>
                                            </span>
                                            {!hasSelectedItemRows && (
                                                <UncontrolledTooltip
                                                    placement="top"
                                                    target="order-return-items-export-tooltip">
                                                    {exportDisabledTooltip}
                                                </UncontrolledTooltip>
                                            )}
                                            <DropdownMenu right>
                                                <DropdownItem
                                                    onClick={handlePrintItems}
                                                    disabled={isPrintPDFLoading || !hasSelectedItemRows}>
                                                    {isPrintPDFLoading ? 'Printing...' : 'Create Packing Slip'}
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() =>
                                                        handleExportItems(
                                                            'excel',
                                                            '/orderReturns/export',
                                                            'order-returns.xlsx'
                                                        )
                                                    }
                                                    disabled={
                                                        (isExportFileLoading && activeExport === 'excel') ||
                                                        !hasSelectedItemRows
                                                    }>
                                                    {isExportFileLoading && activeExport === 'excel'
                                                        ? 'Exporting...'
                                                        : 'Export Excel'}
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    </div>
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </Card>
            {selectedRecord && (
                <Card className="p-2 mt-3" innerRef={detailRef}>
                    <div className="mb-3">
                        <h4>Return #{getParentId(selectedRecord)}</h4>
                        <div>
                            <strong>Facility:</strong> {pick(selectedRecord, ['facId'])}
                        </div>
                        <div>
                            <strong>Form Date:</strong> {formatDate(pick(selectedRecord, ['formDate'], null))}
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', width: '100%' }}>
                        <Table bordered style={{ minWidth: '1000px', whiteSpace: 'nowrap', marginBottom: 0 }}>
                            <thead className="thead-light">
                                <tr>
                                    <th className="p-3">Rx #</th>
                                    <th className="p-3">Resident ID</th>
                                    <th className="p-3">Resident</th>
                                    <th className="p-3">Drug Name</th>
                                    <th className="p-3">Dispense Date</th>
                                    <th className="p-3">Return Qty</th>
                                    <th className="p-3">Destruction Only</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedItems.length > 0 ? (
                                    selectedItems.map((drug, idx) => (
                                        <tr key={drug?.id ?? idx}>
                                            <td className="p-3">{pick(drug, ['rxNumber'])}</td>
                                            <td className="p-3">{pick(drug, ['patientId'])}</td>
                                            <td className="p-3">{pick(drug, ['patientName'])}</td>
                                            <td className="p-3">{pick(drug, ['drugLabelName'])}</td>
                                            <td className="p-3">{formatDate(pick(drug, ['dispenseDate'], null))}</td>
                                            <td className="p-3">{pick(drug, ['amount'])}</td>
                                            <td className="p-3">
                                                {pick(drug, ['isDestructionOnly', 'IsDestructionOnly'], false) ? (
                                                    <span
                                                        role="checkbox"
                                                        aria-checked="true"
                                                        aria-label="Destruction only"
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            backgroundColor: '#2563EB',
                                                            borderRadius: '3px',
                                                            color: '#fff',
                                                            fontSize: '11px',
                                                            lineHeight: 1,
                                                        }}>
                                                        &#10003;
                                                    </span>
                                                ) : (
                                                    <input
                                                        type="checkbox"
                                                        checked={false}
                                                        readOnly
                                                        disabled
                                                        aria-label="Destruction only"
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center p-4">
                                            No items found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    <div className="d-flex justify-content-end mt-3" style={{ gap: '10px' }}>
                        {canDeleteOrderReturn && (
                            <Button
                                size="sm"
                                color="danger"
                                outline
                                onClick={() => handleDeleteReturn(selectedRecord)}
                                disabled={isDeleteOrderReturnLoading}>
                                {isDeleteOrderReturnLoading ? <ButtonLoader /> : 'Delete'}
                            </Button>
                        )}
                        {canEditOrderReturn && (
                            <Button size="sm" color="primary" onClick={() => handleEditReturn(selectedRecord)}>
                                Edit
                            </Button>
                        )}
                    </div>
                </Card>
            )}

            <CreatePDFModal
                isCreatePDFModalOpen={isCreatePDFModalOpen}
                setIsCreatePDFModalOpen={setIsCreatePDFModalOpen}
                selectedRows={selectedItemsForExport}
                setSelectedRows={setSelectedRows}
                printPDFResponse={printPDFResponse}
                refetchData={refetchSavedOrderReturns}
            />
        </div>
    );
}

export default SavedOrderReturns;
